import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Wrench } from 'lucide-react';
import { api, type Monitor } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/StatusDot';
import { formatMs, formatRelative } from '@/lib/utils';

export function Dashboard() {
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.listMonitors();
      setMonitors(res.monitors);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  const up = monitors.filter((m) => m.status === 'up').length;
  const down = monitors.filter((m) => m.status === 'down').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Monitors</h1>
          <p className="text-sm text-muted-foreground">
            {monitors.length} total · {up} up · {down} down
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Refresh
          </Button>
          <Link
            to="/admin/monitors/new"
            className="inline-flex items-center gap-2 h-8 rounded-md bg-primary text-primary-foreground px-3 text-xs font-medium hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            New monitor
          </Link>
        </div>
      </div>

      {error && (
        <Card className="p-4 border-destructive/50 text-destructive">{error}</Card>
      )}

      {!monitors.length && !loading && (
        <Card className="p-12 text-center text-muted-foreground">
          No monitors yet.{' '}
          <Link to="/admin/monitors/new" className="text-primary underline underline-offset-4">
            Create your first monitor
          </Link>
          .
        </Card>
      )}

      <div className="grid gap-3">
        {monitors.map((m) => (
          <Link key={m.id} to={`/admin/monitors/${m.id}`} className="block">
            <Card className="p-4 hover:bg-accent/40 transition-colors">
              <div className="flex items-center gap-4">
                <StatusDot status={m.status ?? 'unknown'} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{m.name}</span>
                    {m.public ? (
                      <Badge variant="secondary" className="text-[10px]">
                        PUBLIC
                      </Badge>
                    ) : null}
                    {!m.enabled && (
                      <Badge variant="outline" className="text-[10px]">
                        PAUSED
                      </Badge>
                    )}
                    {m.maintenance && (
                      <Badge variant="secondary" className="text-[10px] gap-1">
                        <Wrench className="h-3 w-3" />
                        MAINTENANCE
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.url}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="tabular-nums">{formatMs(m.last_duration_ms)}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatRelative(m.last_check_at)}
                  </div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
